import { FacturationResponse } from '../common/models/facturationResponse';
import { EventEmitter, Injectable } from '@angular/core';
import { FormExtractorService } from '../../shared/services/form-extractor.service';
import { HttpService } from '../../shared/services/http.service';
import { LogService } from '../../shared/services/log.service';
import { CTRL_NAME_EXTRACTOR } from '../validators/ctrl.name.extractor';
import { StepperBuilderService } from '../../avisDePub/creer/services/stepper.builder.service';
import { environment } from '../../../../environments/environment';
import { Observable, Observer, of } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class FacturationApiService {

  constructor(private http: HttpService, private formExtractorService: FormExtractorService,
              private stepperBuilderService: StepperBuilderService) {
  }

  // Mirroir des messages FacturationServiceImpl.MSG_ERR_IDENTIFIANT_ADRESSAGE_OBLIGATOIRE et
  // MSG_ERR_IDENTIFIANT_ADRESSAGE_NON_CONFORME (avis-de-publication-services) : depuis l'ajout de la
  // règle rule="required|minSize_9|maxSize_125" sur le champ Identifiant d'adressage
  // (type-adresseTEMPOST.component.html, REQ3936109), ces deux RG sont déjà signalées localement
  // (astérisque + message inline sous le champ, via le scan générique isFormValid()) - filtrées
  // ci-dessous pour ne pas les afficher une 2e fois dans le bandeau de l'étape.
  // Attention à modifier les messages ici si leur pendant est modifié dans le back (leur Javadoc, dans
  // FacturationServiceImpl.java, pointe symétriquement vers ces constantes) - comparaison sur la
  // chaîne exacte, apostrophe typographique comprise.
  private static readonly MSG_BACKEND_IDENTIFIANT_ADRESSAGE_OBLIGATOIRE =
    'L\'identifiant d\'adressage est obligatoire pour une entité privée assujettie à la TVA.';
  private static readonly MSG_BACKEND_IDENTIFIANT_ADRESSAGE_NON_CONFORME =
    'L\'identifiant d\'adressage doit comporter de 9 à 125 caractères.';
  public facturationResponse: EventEmitter<FacturationResponse> = new EventEmitter();
  private LOG: LogService = new LogService(FacturationApiService);

  public validate(form: any, referenceAvis: any): Observable<FacturationResponse> {
    if (environment.check_facturation === 'false') {
      return of(null);
    }

    const dataFacturation = this.formExtractorService.getValueFacturationByFilter(form);
    // Si le SIRET n'est pas au bon format, on s'arrête là pour ne pas afficher
    // les mêmes erreurs en doublon à l'utilisateur (car back et front font les mêmes contrôles).
    if (this.prerequisFacturationNonSatisfaits(dataFacturation)) {
      return of(null);
    }

    return new Observable<FacturationResponse>(observer => {
      this.checkDataFacturation(form, dataFacturation, referenceAvis, observer);
    });
  }

  /**
   * SIRET absent ou mal formé, ou type d'entité non déterminé : le back ne peut rien contrôler de plus
   * (checkSiretAndTypeEntiteAvailable bloque et court-circuite toute la chaîne de vérification), et ses
   * deux messages — « Le numéro de SIRET est incorrect. Merci de le corriger et réessayer. » et « Le type d'entité est obligatoire. » —
   * feraient doublon avec les contrôles locaux déjà affichés dans le bandeau de l'étape : ctr_1210
   * (obligatoire) / ctr_1216 (format), et ctr_type_entite_obligatoire, ce dernier étant plus précis
   * puisqu'il renvoie vers le bouton « Récupérer les données ».
   * Même raison que le court-circuit de TypeAdresseTEMPOSTComponent.verifierSiret() sur un SIRET
   * invalide : inutile d'appeler l'API
   * Le format doit rester strictement aligné sur SiretUtils côté back (14 chiffres, sans séparateur).
   */
  private prerequisFacturationNonSatisfaits(dataFacturation: any): boolean {
    const siret: string = dataFacturation?.siret;
    const typeEntite: string = dataFacturation?.typeEntite;
    return !siret || !/^\d{14}$/.test(siret) || !typeEntite;
  }

   checkDataFacturation(form: any, dataFacturation: any, referenceAvis: any, observer: Observer<FacturationResponse>) {
    const endpoint = '/facturation';
    const ctrlFacturation = 'CTRL_FACTURATION';
    const infoFacturation = this.completeInfosFacturation(dataFacturation, referenceAvis);

    this.http.post(endpoint, infoFacturation, FacturationResponse)
      .subscribe(
        (data) => {
          const rgs: string[] = CTRL_NAME_EXTRACTOR.getCtrlNames(form, ctrlFacturation);
          if (rgs.length > 0) {
            // Retrait des erreurs back précédemment ajoutées
            this.stepperBuilderService.removeFieldFromStepErrors('_STEP_ID_SepSep_TypeFacturation', '');
            if (data.status === 'KO') {
              if (data.erreursBloquantes && data.erreursBloquantes.length !== 0) {
                // cf. commentaire sur MSG_BACKEND_IDENTIFIANT_ADRESSAGE_OBLIGATOIRE/_NON_CONFORME : déjà
                // signalées localement par rule="required|minSize_9|maxSize_125" sur ce champ précis,
                // ne pas les dupliquer dans le bandeau.
                const identifiantAdressageLength = (dataFacturation.identifiantAdressage || '').length;
                const identifiantAdressageDejaSignaleLocalement = dataFacturation.typeEntite === 'PriveeAssujettie'
                  && (!dataFacturation.identifiantAdressage || identifiantAdressageLength < 9 || identifiantAdressageLength > 125);
                data.erreursBloquantes
                  .filter(error => !(identifiantAdressageDejaSignaleLocalement
                    && (error.toString() === FacturationApiService.MSG_BACKEND_IDENTIFIANT_ADRESSAGE_OBLIGATOIRE
                      || error.toString() === FacturationApiService.MSG_BACKEND_IDENTIFIANT_ADRESSAGE_NON_CONFORME)))
                  .forEach(error => {
                    this.stepperBuilderService.addStepFieldsOnErrors('_STEP_ID_SepSep_TypeFacturation', '', error.toString());
                  });
              }
              if (data.erreursNonBloquantes && data.erreursNonBloquantes.length !== 0) {
                this.stepperBuilderService.clearStepFieldsOnWarnings('_STEP_ID_SepSep_TypeFacturation');
                data.erreursNonBloquantes.forEach(warning => {
                  this.stepperBuilderService.addStepFieldsOnWarnings('_STEP_ID_SepSep_TypeFacturation', '', warning.toString());
                });
              }
            }
            for (const rg of rgs) {
              if (data.status === 'KO') {
                form.controls[rg].value = [];
              }
              if ((!data.erreursBloquantes || data.erreursBloquantes.length === 0)
                && (!data.erreursNonBloquantes || data.erreursNonBloquantes.length === 0)) {
                form.controls[rg].value = {
                  rgCtrls: ctrlFacturation,
                  ctrlValidation: false,
                  ctrlValidationWarning: false
                };
              }
            }
          }
          observer.next(data);
          observer.complete();
        },
        (error) => {
          this.LOG.error('Appel back check facturation KO : ', error);
          observer.error(error);
        }
      );
  }

  completeInfosFacturation(dataFacturation: any, referenceAvis: any) {
    const siret = dataFacturation.siret;
    const service = dataFacturation.service;
    const engagementJuridique = dataFacturation.engagementJuridique;
    const classeProfil = dataFacturation.classeProfil;
    const typeEntite = dataFacturation.typeEntite;
    const identifiantAdressage = dataFacturation.identifiantAdressage;

    return {siret, service, engagementJuridique, classeProfil, typeEntite, identifiantAdressage, referenceAvis};
  }
}
